import CardTile from "../CardTile";
import { getDefaultListingMode, getResolvedListingOption } from "../features/traders/listingPricing";

export default function CardGrid({
  cards,
  selectedCards,
  toggleSelect,
  onCardClick,
  isCardDisabled,
  badgeType, // string eller funktion(card)
  renderCardAction,
  showPricePanel = false,
  listingOptions,
  setListingOptions,
  pricePanelTitle = "Listing price",
  editMode = false,
  pendingEdits = {},
  setPendingEdits,
  onEditCard,
  preferNormalImage = false,
  stackOverlayActionOnMobile = false
}) {
  return (
    <div className="card-grid">
      {cards.map(card => {
        const isSelected = selectedCards?.includes(card.id);
        const cardDisabled = Boolean(isCardDisabled?.(card));
        const resolvedListingOption = getResolvedListingOption(card, listingOptions);
        const resolvedSalePrice = resolvedListingOption.mode === "trend"
          ? card.price_trend
          : resolvedListingOption.mode === "foiltrend"
            ? card.price_foil
            : resolvedListingOption.customPrice;
        const badge = typeof badgeType === "function" ? badgeType(card) : badgeType;
        const isLockedListedCard = badge === "LISTED" && Boolean(card.isTradeLocked);
        return (
          <div
            className="card-grid__item"
            key={card.id}
            onClick={editMode
              ? (() => !isLockedListedCard && onEditCard && onEditCard(card))
              : (onCardClick
                  ? (() => !cardDisabled && onCardClick(card))
                  : (toggleSelect
                      ? (() => toggleSelect(card.id))
                      : undefined))}
            style={{
              position: "relative",
              cursor: cardDisabled
                ? "not-allowed"
                : (editMode ? (isLockedListedCard ? "not-allowed" : "pointer") : ((onCardClick || toggleSelect) ? "pointer" : "default")),
              borderRadius: "8px",
              transition: "0.15s",
              padding: "2px",
              minHeight: "var(--card-grid-item-height)",
              height: "auto",
              overflow: "visible",
              boxShadow: isSelected ? "0 0 0 3px #1976d2" : "none",
              opacity: cardDisabled ? 0.55 : 1,
              filter: cardDisabled ? "grayscale(0.45)" : "none"
            }}
          >
            {cardDisabled ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(15, 23, 42, 0.18)",
                  zIndex: 11,
                  pointerEvents: "none"
                }}
              />
            ) : null}
            {/* Checkmark */}
            {isSelected && (
              <div
                style={{
                  position: "absolute",
                  top: 5,
                  right: 5,
                  background: "#1976d2",
                  color: "white",
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: "bold",
                  zIndex: 10
                }}
              >
                ✓
              </div>
            )}
            <CardTile
              card={{
                ...card,
                isBrowseTradeBlocked: cardDisabled || Boolean(card.isBrowseTradeBlocked),
                browseTradeBlockedLabel: card.browseTradeBlockedLabel,
                pricing_mode: badge === "LISTED" && isSelected
                  ? resolvedListingOption.mode
                  : card.pricing_mode,
                sale_price:
                  badge === "LISTED" && isSelected
                    ? resolvedSalePrice
                    : card.sale_price
              }}
              badgeType={badge}
              salePrice={badge === "LISTED" && isSelected
                ? resolvedSalePrice
                : card.sale_price}
              overlayAction={renderCardAction ? renderCardAction({ card, cardDisabled }) : null}
              editMode={editMode}
              pendingEdits={pendingEdits}
              setPendingEdits={setPendingEdits}
              preferNormalImage={preferNormalImage}
              stackOverlayActionOnMobile={stackOverlayActionOnMobile}
              showCustomPriceComparison={badge === "LISTED"}
            />
            {/* Price panel (valgfrit) */}
            {showPricePanel && isSelected && !isLockedListedCard && (
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: "8px",
                  background: "#eef4ff",
                  borderRadius: "6px",
                  zIndex: 10,
                  fontSize: "12px",
                  maxHeight: "150px",
                  overflowY: "auto"
                }}
                onClick={e => e.stopPropagation()}
              >
                <strong>{pricePanelTitle}</strong>
                {/* Trend price */}
                <label style={{ display: "block", marginTop: "4px", fontSize: "11px" }}>
                  <input
                    type="radio"
                    name={`priceMode-${card.id}`}
                    checked={resolvedListingOption.mode === "trend"}
                    disabled={Boolean(card.foil)}
                    onChange={() =>
                      !card.foil && setListingOptions && setListingOptions(prev => ({
                        ...prev,
                        [card.id]: { mode: "trend", customPrice: "" }
                      }))
                    }
                  />
                  {" "}Trend: {card.price_trend} €
                </label>
                {/* Foil Trend price */}
                <label style={{ display: "block", marginTop: "4px", fontSize: "11px", color: card.foil && Number(card.price_foil) > 0 ? undefined : '#aaa' }}>
                  <input
                    type="radio"
                    name={`priceMode-${card.id}`}
                    checked={resolvedListingOption.mode === "foiltrend"}
                    disabled={!card.foil || Number(card.price_foil) <= 0}
                    onChange={() =>
                      card.foil && Number(card.price_foil) > 0 && setListingOptions && setListingOptions(prev => ({
                        ...prev,
                        [card.id]: { mode: "foiltrend", customPrice: "" }
                      }))
                    }
                  />
                  {" "}Foil Trend: {card.price_foil ? card.price_foil + " €" : "— €"}
                </label>
                {/* Custom price */}
                <label style={{ display: "block", marginTop: "4px", fontSize: "11px" }}>
                  <input
                    type="radio"
                    name={`priceMode-${card.id}`}
                    checked={resolvedListingOption.mode === "custom"}
                    onChange={() =>
                      setListingOptions && setListingOptions(prev => ({
                        ...prev,
                        [card.id]: { ...prev[card.id], mode: "custom", customPrice: "" }
                      }))
                    }
                  />
                  {" "}Custom:
                </label>
                {resolvedListingOption.mode === "custom" && (
                  <input
                    type="number"
                    step="0.01"
                    placeholder="€"
                    value={resolvedListingOption.customPrice || ""}
                    onChange={e =>
                      setListingOptions && setListingOptions(prev => ({
                        ...prev,
                        [card.id]: {
                          mode: getDefaultListingMode(card),
                          ...prev[card.id],
                          customPrice: e.target.value
                        }
                      }))
                    }
                    style={{
                      marginTop: "4px",
                      padding: "3px",
                      width: "100%",
                      borderRadius: "3px",
                      border: "1px solid #ccc",
                      fontSize: "11px"
                    }}
                  />
                )}
              </div>
            )}
            {showPricePanel && isSelected && isLockedListedCard ? (
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: "10px 12px",
                  background: card.isPendingTradeLocked ? "#fff7ed" : "#eff6ff",
                  borderRadius: "6px",
                  zIndex: 10,
                  fontSize: "12px",
                  fontWeight: 700,
                  color: card.isPendingTradeLocked ? "#9a3412" : "#1d4ed8"
                }}
              >
                {card.tradeLockLabel || "Locked in trade"}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}